import { get, isBoolean, pickBy, uniq } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import Avatar, { IAvatar } from '../Avatar';
import Button from '../../../Button';
import ClientCardSkeleton from '../ClientCardSkeleton';
import CreateTicketButton from '../../Tickets/CreateTicketButton';
import EditableField, {
  StyledInput,
  StyledPlaceholder,
  StyledValue,
} from '../../Common/EditableField';
import Messenger, { Types } from '../../Chains/Messenger';
import Name from '../Name';

import { AvatarCrown, PhoneOpen } from '@icons/common';
import { ValueOf } from '@utils/tsUtils';
import { CHAIN_SERVICES } from '@constants/chains';
import { serializeChainType } from '../../../../store/serializers/chain';
import ClientLabelsSelector from '../ClientLabelsSelector';
import { OptionsType } from '@components/CRM/Tickets/types';

import { AlfaPartner } from '@icons/communication';

const VALIDATION_PATTERNS = {
  CYRILLIC: /^[\u0400-\u04FF]*$/g,
  NUMBERS: /^[0-9]*$/g,
  EMAIL: /\S+@\S+\.\S+/,
};

const VALIDATION_RULES = {
  NAME: {
    error: 'Только кириллица',
    pattern: VALIDATION_PATTERNS.CYRILLIC,
  },
  SURNAME: {
    error: 'Только кириллица',
    pattern: VALIDATION_PATTERNS.CYRILLIC,
  },
  PATRONYMIC: {
    error: 'Только кириллица',
    pattern: VALIDATION_PATTERNS.CYRILLIC,
  },
  PHONE: {
    error: 'Только цифры',
    pattern: VALIDATION_PATTERNS.NUMBERS,
  },
  EMAIL: {
    pattern: VALIDATION_PATTERNS.EMAIL,
  },
};

interface ClientCardProps {
  className?: string;
  active: boolean;
  avatarUrl: IAvatar['url'];
  callable: boolean;
  email: string | null;
  errors: any;
  forceEditable: boolean | null;
  fullName?: string;
  hasPriorityService?: boolean;
  id: string | null;
  isLoading: boolean;
  isDeleted?: boolean;
  isNew: boolean;
  isPartnerClient?: boolean;
  isSegmentAllowed?: boolean | null;
  messengers: Array<{ type: string; active: boolean }> | null;
  name: string;
  partner: string | null;
  phone: string | null;
  surname: string;
  patronymic: string | null;
  ticketable: boolean;
  type?: string;
  labels: OptionsType['labels'];

  onCallClick: ({ value }: { value: string }) => void;
  onFocusField: () => void;
  onMessengerClick: (data: { type: ValueOf<typeof Types> }) => void;
  onNewTicketClick: ({ clientId }: { clientId: string }) => void;
  onSave: (data: any) => void;
}

interface ClientCardState {
  email: ClientCardProps['email'];
  isEmailEditable: ClientCardProps['forceEditable'];
  isNameEditable: ClientCardProps['forceEditable'];
  isPhoneEditable: ClientCardProps['forceEditable'];
  isSurnameEditable: ClientCardProps['forceEditable'];
  isPatronymicEditable: ClientCardProps['forceEditable'];
  name: ClientCardProps['name'];
  phone: ClientCardProps['phone'];
  showErrors: boolean;
  surname: ClientCardProps['surname'];
  patronymic: ClientCardProps['patronymic'];
  labelIds: number[];
  readOnlyLabels: boolean;
}

class ClientCard extends React.PureComponent<ClientCardProps, ClientCardState> {
  static defaultProps = {
    active: false,
    avatarUrl: '',
    callable: true,
    className: '',
    email: null,
    errors: {},
    forceEditable: false,
    id: null,
    isLoading: true,
    isNew: true,
    messengers: null,
    name: '',
    onCallClick: Function.prototype,
    onFocusField: Function.prototype,
    onMessengerClick: Function.prototype,
    onNewTicketClick: Function.prototype,
    onSave: Function.prototype,
    phone: null,
    surname: '',
    partner: null,
    ticketable: false,
    isSegmentAllowed: false,
    type: 'client',
    fullName: '',
  };

  static getDynamicStateFields(aProps, bProps) {
    const state = {} as ClientCardState;

    const hasChanges =
      JSON.stringify({
        ...aProps,
        labelIds: aProps?.labelIds,
      }) !==
      JSON.stringify({
        ...bProps,
        labelIds: bProps?.labelIds,
      });

    const { forceEditable: isForceEditable } = aProps;

    if (hasChanges) {
      state.email = get(aProps, 'email');
      state.isEmailEditable = isBoolean(isForceEditable)
        ? isForceEditable
        : true;
      state.isNameEditable = isBoolean(isForceEditable)
        ? isForceEditable
        : !aProps.name;
      state.isPhoneEditable = isBoolean(isForceEditable)
        ? isForceEditable
        : !aProps.phone;
      state.isSurnameEditable = isBoolean(isForceEditable)
        ? isForceEditable
        : !aProps.surname;
      state.isPatronymicEditable = isBoolean(isForceEditable)
        ? isForceEditable
        : !aProps.patronymic;
      state.name = get(aProps, 'name');
      state.phone = get(aProps, 'phone');
      state.showErrors = false;
      state.surname = get(aProps, 'surname');
      state.patronymic = get(aProps, 'patronymic');
      state.labelIds = get(aProps, 'labelIds') || [];

      state.readOnlyLabels = false;
    }

    return state;
  }

  constructor(props) {
    super(props);

    const dynamicStateFields = ClientCard.getDynamicStateFields(
      props,
      undefined
    );

    this.state = {
      ...dynamicStateFields,
      readOnlyLabels: false,
    };

    this.handleCallClick = this.handleCallClick.bind(this);
    this.handleChangeField = this.handleChangeField.bind(this);
    this.handleFocusField = this.handleFocusField.bind(this);
    this.handleSaveField = this.handleSaveField.bind(this);
    this.handleLabelChange = this.handleLabelChange.bind(this);
    this.handleResetField = this.handleResetField.bind(this);
    this.phoneFieldRenderer = this.phoneFieldRenderer.bind(this);
    this.handleNewTicketClick = this.handleNewTicketClick.bind(this);
    this.handleSave = this.handleSave.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props !== prevProps || this.state !== prevState) {
      this.setState(() =>
        ClientCard.getDynamicStateFields(this.props, prevProps)
      );
    }
  }

  handleCallClick() {
    const { phone: value } = this.state;
    const { onCallClick } = this.props;
    if (!value) return;

    onCallClick({ value });
  }

  handleMessengerClick({ type }) {
    const { onMessengerClick } = this.props;
    onMessengerClick({ type });
  }

  handleChangeField({ target }) {
    const { name, value } = target;
    this.setState(prevState => ({
      ...prevState,
      [name as keyof ClientCardState]: value,
    }));
  }

  handleFocusField() {
    const { onFocusField } = this.props;
    this.setState({ showErrors: false }, onFocusField);
  }

  handleSaveField({ name, value }) {
    const { isNew } = this.props;
    this.setState(
      prevState => ({
        ...prevState,
        [name as keyof ClientCardState]: value,
      }),
      !isNew ? () => this.handleSave() : undefined
    );
  }

  handleResetField({ name }) {
    const oldValue = get(this.props, name);
    this.setState(prevState => ({
      ...prevState,
      [name as keyof ClientCardState]: oldValue,
      showErrors: false,
    }));
  }

  handleLabelChange({ target }) {
    const { value } = target;
    const labelIds: number[] = uniq(value) || [];
    this.setState({ labelIds, readOnlyLabels: true }, () => this.handleSave());
  }

  handleNewTicketClick() {
    const { id: clientId, onNewTicketClick } = this.props;
    if (!clientId) return;
    onNewTicketClick({ clientId });
  }

  handleSave() {
    const { id, onSave } = this.props;
    const {
      name,
      surname,
      patronymic,
      phone,
      email,
      labelIds: label_ids,
    } = this.state;

    const savingParams = pickBy(
      {
        id,
        name,
        surname,
        patronymic,
        phone,
        email,
        label_ids,
      },
      v => v && v !== ''
    );

    onSave(savingParams);

    this.setState({
      showErrors: true,
    });
  }

  isChanged() {
    const fields = ['name', 'surname', 'patronymic', 'phone', 'email'];
    const { email, phone } = this.state;
    const isFilled = email || phone;

    return (
      isFilled &&
      fields.some(fieldName => this.props[fieldName] !== this.state[fieldName])
    );
  }

  phoneFieldRenderer() {
    const { phone } = this.state;
    const { callable, isPartnerClient } = this.props;
    const disabled = !callable || isPartnerClient;
    return (
      phone && (
        <StyledPhoneButton onClick={this.handleCallClick} disabled={disabled}>
          <StyledLabel>{phone}</StyledLabel>
          <StyledPhoneIcon />
        </StyledPhoneButton>
      )
    );
  }

  render() {
    const {
      email: stateEmail,
      isEmailEditable,
      isNameEditable,
      isPhoneEditable,
      isSurnameEditable,
      isPatronymicEditable,
      name: stateName,
      phone: statePhone,
      showErrors,
      surname: stateSurname,
      patronymic: statePatronymic,
      labelIds,
      readOnlyLabels,
    } = this.state;
    const {
      name: propName,
      surname: propSurname,
      patronymic: propPatronymic,
    } = this.props;
    const {
      active,
      avatarUrl,
      email,
      errors,
      hasPriorityService,
      id,
      isDeleted,
      isLoading,
      isNew,
      isPartnerClient,
      messengers,
      partner,
      name,
      ticketable,
      labels,
      ...props
    } = this.props;
    const hasMessengers = messengers && !!messengers.length && !isPartnerClient;

    if (isLoading) {
      return <StyledSkeleton />;
    }

    return (
      <StyledClientCard {...props}>
        {isDeleted && (
          <StyledDeactivatedWarn>Клиент деактивирован</StyledDeactivatedWarn>
        )}
        <div>
          <StyledAbout>
            <StyledAvatarContainer>
              {hasPriorityService && (
                <StyledAvatarCrownIcon width={19} height={14} />
              )}
              <StyledAvatar name={name} url={avatarUrl} pulsing={active} />
            </StyledAvatarContainer>
            <StyledInfo>
              {isNameEditable ? (
                <StyledBaseEditor
                  error={showErrors && errors.name}
                  name="name"
                  onChange={this.handleChangeField}
                  onFocus={this.handleFocusField}
                  onReset={this.handleResetField}
                  onSave={this.handleSaveField}
                  placeholder="Имя"
                  preventClose={!isNew}
                  validation={VALIDATION_RULES.NAME}
                  value={stateName}
                />
              ) : (
                <StyledName name={propName} />
              )}
              {isSurnameEditable ? (
                <StyledBaseEditor
                  error={showErrors && errors.surname}
                  name="surname"
                  onChange={this.handleChangeField}
                  onFocus={this.handleFocusField}
                  onReset={this.handleResetField}
                  onSave={this.handleSaveField}
                  placeholder="Фамилия"
                  preventClose={!isNew}
                  validation={VALIDATION_RULES.SURNAME}
                  value={stateSurname}
                />
              ) : (
                <StyledName name={propSurname} />
              )}

              {propPatronymic &&
                (isPatronymicEditable ? (
                  <StyledBaseEditor
                    error={showErrors && errors.patronymic}
                    name="patronymic"
                    onChange={this.handleChangeField}
                    onFocus={this.handleFocusField}
                    onReset={this.handleResetField}
                    onSave={this.handleSaveField}
                    placeholder="Отчество"
                    preventClose={!isNew}
                    validation={VALIDATION_RULES.PATRONYMIC}
                    value={statePatronymic}
                  />
                ) : (
                  <StyledName name={propPatronymic} />
                ))}

              {id && (
                <StyledId>
                  {partner === 'alfa' && <StyledAlfaPartner />}#{id}
                </StyledId>
              )}
            </StyledInfo>
            {ticketable && (
              <CreateTicketButton onClick={this.handleNewTicketClick} />
            )}
          </StyledAbout>
          <StyledClientLabelsSelector
            key={JSON.stringify(labels)}
            isSearchable
            onChange={this.handleLabelChange}
            labels={labels}
            value={labelIds}
            readOnly={!!labelIds.length && readOnlyLabels}
          />
        </div>
        <EditableField
          error={showErrors && errors.phone}
          label="Телефон"
          name="phone"
          onChange={this.handleChangeField}
          onFocus={this.handleFocusField}
          onReset={this.handleResetField}
          onSave={this.handleSaveField}
          placeholder="Укажите телефон"
          preventClose={!isNew}
          readOnly={!isPhoneEditable}
          validation={VALIDATION_RULES.PHONE}
          value={statePhone}
        >
          {this.phoneFieldRenderer()}
        </EditableField>
        <StyledEditableField
          error={showErrors && errors.email}
          label="E-mail"
          name="email"
          onChange={this.handleChangeField}
          onFocus={this.handleFocusField}
          onOpen={() =>
            this.handleMessengerClick({ type: CHAIN_SERVICES.EMAIL })
          }
          onReset={this.handleResetField}
          onSave={this.handleSaveField}
          placeholder="Укажите почту"
          preventClose={!isNew}
          readOnly={!isEmailEditable}
          disabled={isPartnerClient}
          required={!!email}
          validation={VALIDATION_RULES.EMAIL}
          value={stateEmail}
        />
        {hasMessengers && (
          <StyledField>
            <StyleLabel>Чаты</StyleLabel>
            <StyledMessengers>
              {messengers.map(messenger => (
                <StyledMessengerButton
                  disabled={!messenger.active}
                  onClick={() =>
                    this.handleMessengerClick({ type: messenger.type })
                  }
                >
                  <Messenger type={serializeChainType(messenger.type)} />
                </StyledMessengerButton>
              ))}
            </StyledMessengers>
          </StyledField>
        )}
        {isNew && (
          <StyledSubmitButton
            disabled={!this.isChanged()}
            onClick={this.handleSave}
          >
            Сохранить данные клиента
          </StyledSubmitButton>
        )}
      </StyledClientCard>
    );
  }
}

const StyledClientLabelsSelector = styled(ClientLabelsSelector)`
  margin-top: 10px;
  min-height: 30px;
  line-height: 30px;

  ${props =>
    props.readOnly &&
    `
    opacity: .5;
    pointer-events: none;
  `}
`;

const StyledClientCard = styled.div`
  min-width: 250px;
  padding: 10px;
  width: 100%;
`;

const StyledDeactivatedWarn = styled.div`
  padding: 10px;
  background: rgb(245, 243, 242);
  border-radius: 4px;
  margin-bottom: 10px;
  text-align: center;
  color: #cd5555;
`;

const StyledAbout = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const StyledInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
  height: 40px;
  width: 40px;
  z-index: 0;
`;

const StyledAvatarContainer = styled.div`
  flex-shrink: 0;
  height: 40px;
  margin: 2px 9px 0 0;
  position: relative;
  width: 40px;
`;

const StyledAvatarCrownIcon = styled(AvatarCrown)`
  left: 0;
  position: absolute;
  top: -8px;
  transform: rotate(-25deg);
  z-index: 1;
`;

const StyledName = styled(Name)`
  font-size: 18px;
`;

const StyledId = styled.div`
  color: #919499;
  font-size: 12px;
  margin: 3px 0 -3px 0;
  align-items: center;
  display: flex;
`;

const StyledAlfaPartner = styled(AlfaPartner)`
  height: 17px;
  margin-right: 4px;
  width: 17px;
`;

const StyledField = styled.div`
  align-items: flex-start;
  display: flex;
  margin-bottom: 5px;
`;

const StyledEditableField = styled(EditableField)`
  height: 25px;
  margin-bottom: 5px;
`;

const StyledBaseEditor = styled(EditableField)`
  height: 22px;

  ${StyledInput} {
    font-size: 18px;
    font-weight: bold;
    &::placeholder {
      color: #c2c5cc;
      font-weight: bold;
    }
  }

  ${StyledPlaceholder} {
    color: #c2c5cc;
    font-size: 18px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${StyledValue} {
    color: black;
    cursor: default;
    font-size: 18px;
    font-weight: bold;
    text-decoration: none;
  }
`;

const StyleLabel = styled.div`
  width: 100px;
  margin-top: 1px;
  font-size: 14px;
`;

const StyledMessengers = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 -5px;
`;

const StyledMessengerButton = styled(Button)`
  padding: 0;
  width: 24px;
  height: auto;
  border: none;
  background: none;
  margin-right: 6px;
  margin-bottom: 5px;

  &:disabled {
    cursor: not-allowed;
    filter: grayscale(100%);
    opacity: 0.5;
  }
`;

const StyledPhoneIcon = styled(PhoneOpen)`
  width: 21px;
  height: 15px;
  margin-left: 6px;
  display: inline-block;
  vertical-align: middle;
`;

const StyledPhoneButton = styled(Button)`
  background: white;
  font-family: inherit;
  font-size: 14px;
  height: 36px;
  text-align: center;
  text-decoration: none;
  width: 169px;

  &:hover {
    color: #007aff;
    border-color: #007aff;
  }

  &:disabled {
    pointer-events: none;
    user-select: all;
    svg path {
      stroke: #c2c5cc;
    }
  }
`;

const StyledSubmitButton = styled(Button)`
  width: 100%;
  color: white;
  height: 36px;
  border: none;
  font-size: 13px;
  font-weight: bold;
  background-color: #007aff;

  &:hover {
    color: white;
  }

  &:disabled {
    background-color: #c2c5cc;
  }
`;

const StyledLabel = styled.span`
  height: 17px;
  overflow: hidden;
  max-width: 119px;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
  text-overflow: ellipsis;
`;

const StyledSkeleton = styled(ClientCardSkeleton)`
  height: 157px;
  min-width: 250px;
  padding: 12px 10px;
  width: 100%;
`;

export default ClientCard;
